/* =========================================================
   BYO Variety Pack Builder – JS
   Uses Shopify Cart AJAX API with nested cart lines
   (parent_id) so flavors appear as children of the bundle.
   ========================================================= */

/* The snippet can render twice on one page (PDP + routine sheet), so run only once. */
if (!window.BYOBuilder) {
class BYOBuilder {
  constructor(root) {
    this.root = root;
    this.root.__byoBuilder = this;
    /* one signal for every window/document listener, so destroy() can remove them (section reload in the theme editor) */
    this._abort = new AbortController();
    /* Embedded mode: the builder lives in the PDP Routines sheet. It must not touch the page around it
       (gallery, title, price, URL, scroll position) and it reports back with events instead of leaving the page. */
    this.embedded = !!root.closest('[data-byo-embedded]');
    this.minSelections = parseInt(root.dataset.minSelections, 10) || 6;
    this.maxSelections = parseInt(root.dataset.maxSelections, 10) || 10;
    if (this.minSelections > this.maxSelections) this.minSelections = this.maxSelections;
    this.parentVariantId = parseInt(root.dataset.parentVariantId, 10);
    this.parentEnablesBundledWith =
      root.dataset.parentEnablesBundledWith === 'true' ||
      root.dataset.parentIsByoBundle === 'true';
    this.basePriceCents = parseInt(root.dataset.basePriceCents, 10) || 0;
    this.productPrice = root.dataset.productPrice || '';

    /* Keep originals so we can restore after a Quick Pick is cleared */
    this.originalParentVariantId = this.parentVariantId;
    this.originalBasePriceCents = this.basePriceCents;
    this.originalProductPrice = this.productPrice;
    this.originalProductTitle = root.dataset.productTitle || '';
    this.originalProductUrl = root.dataset.productUrl || '';
    this.pageProductHandle = root.dataset.pageProductHandle || '';

    /* Detect if this builder is rendered inline inside the size selector */
    this.isInline = !!root.closest('.pdp-ss__byo-inline');
    this.isIntegratedPdpByo = this.isInline || !!root.closest('.pdp-size-selector') || !!root.closest('[data-byo-format]');
    this._pdpAtcButton = null;
    this._pdpAtcHandler = null;

    /* Page-level elements updated when Quick Picks are swapped */
    this.pageTitleEl = this.embedded ? null : document.querySelector('.ae--product-title');
    this.pageEyebrowEl = this.embedded ? null : document.querySelector('.ae--product-eyebrow');
    this.pagePriceEl = this.embedded ? null : document.querySelector('.current-price-pdp');

    /* state: { variantId: quantity } */
    this.selections = {};
    this.bundleQty = 1;
    this._qtyDebugCount = 0;

    /* DOM refs */
    this.cards = Array.from(root.querySelectorAll('.byo-card'));
    this.counterEl = root.querySelector('.byo-builder__counter');
    this.counterCurrent = root.querySelector('.byo-builder__counter-current');
    this.counterTarget = root.querySelector('.byo-builder__counter-target');
    this.counterMinLabel = root.querySelector('.byo-builder__counter-min-label');
    this.progressFill = root.querySelector('.byo-builder__progress-fill');
    this.atcButton = root.querySelector('.byo-builder__atc');
    this.footerEl = root.querySelector('.byo-builder__footer');
    this.bundleQtyInput = root.querySelector('.byo-builder__bundle-qty-input');
    this.builderHeader = root.querySelector('.byo-builder__header');
    this.builderTitle = root.querySelector('.byo-builder__title');
    this.originalBuilderTitle = this.builderTitle ? this.builderTitle.textContent.trim() : 'Select Your Flavors';

    /* Collapsible elements */
    this.flavorList = root.querySelector('.byo-builder__list');
    this.categoryLabel = root.querySelector('.byo-builder__category');
    this.categoryGroups = root.querySelector('.byo-builder__category-groups');
    this.categoryAllFlavors = root.querySelector('.byo-builder__category-all-flavors');
    this.defaultCategoryLabel = this.categoryLabel ? this.categoryLabel.textContent.trim() : '';
    this.categoryRow = root.querySelector('.byo-builder__category-row');
    this.progressBar = root.querySelector('.byo-builder__progress');
    this.oosAlert = root.querySelector('.byo-builder__oos-alert');
    this.summaryContainer = root.querySelector('.byo-builder__summary');
    this.summaryItems = root.querySelector('.byo-builder__summary-items');
    this.editBtn = root.querySelector('.byo-builder__edit-btn');
    this.clearBtn = root.querySelector('.byo-builder__clear-btn');
    this.showBtn = root.querySelector('.byo-builder__show-btn');
    this.showLabel = root.querySelector('.byo-builder__show-label');
    this.groupCountEls = Array.from(root.querySelectorAll('[data-group-count]'));
    this.quickPicks = Array.from(root.querySelectorAll('.byo-quick-pick'));
    this.isCollapsed = false;
    this.wasComplete = false;

    /* Cache card data (icon HTML + title + product image) keyed by variantId */
    this.cardData = {};
    this.unavailableVids = new Set();
    this.cards.forEach((card) => {
      const vid = card.dataset.variantId;
      const iconEl = card.querySelector('.byo-card__icon');
      const titleEl = card.querySelector('.byo-card__title');
      this.cardData[vid] = {
        iconHtml: iconEl ? iconEl.innerHTML : '',
        title: titleEl ? titleEl.textContent.trim() : '',
        productImage: card.dataset.productImage || '',
        productId: card.dataset.productId || '',
        handle: card.dataset.productHandle || '',
        pdpColor: card.dataset.pdpColor || '',
        priceAdjustmentCents: parseInt(card.dataset.priceAdjustmentCents, 10) || 0,
        unitPriceCents: parseInt(card.dataset.unitPriceCents, 10) || 0,
        group: card.dataset.cardGroup || 'primary',
      };
      if (card.classList.contains('is-unavailable')) {
        this.unavailableVids.add(vid);
      }
    });

    /* Product media area – cache original content for restore */
    this.productMedia = this.embedded ? null : document.querySelector('.ae--product-media');
    this.originalMediaHtml = this.productMedia ? this.productMedia.innerHTML : '';
    this.mosaicActive = false;

    /* Mobile preview strip */
    this.mobilePreview = root.querySelector('.byo-builder__mobile-preview');
    this.mobilePreviewFan = root.querySelector('.byo-builder__mobile-preview-fan');
    this.mobileCounterCurrent = root.querySelector('.byo-builder__mobile-counter-current');
    this.stickyControls = root.querySelector('.byo-sticky');
    this.mediaOutOfView = false;

    /* Show mobile preview only after scrolling past the product media */
    if (this.productMedia && this.mobilePreview) {
      const headerEl = document.querySelector('.ae--header');
      const headerH = headerEl ? headerEl.offsetHeight : 60;
      this.mediaObserver = new IntersectionObserver(
        ([entry]) => {
          this.mediaOutOfView = !entry.isIntersecting;
          /* Clear the force-fan flag once the media scrolls back into view */
          if (entry.isIntersecting) this._forceFanVisible = false;
          this.updateMobilePreviewVisibility();
        },
        { threshold: 0, rootMargin: `-${headerH}px 0px 0px 0px` }
      );
      this.mediaObserver.observe(this.productMedia);
    }

    /* Set sticky top to clear the fixed site header (and fan preview) */
    this._updateStickyOffsets();

    /* Build handle ↔ variantId lookup maps.
       Strip trailing "-sample" from handles for cleaner URLs.
       Also build a normalized map that strips common product-type
       suffixes so preset handles from metaobjects (which may
       reference the full-size product) can match sample cards. */
    this.handleToVariant = {};
    this.variantToHandle = {};
    this._normalizedHandleMap = {};
    const SUFFIXES_TO_STRIP = ['-sample', '-protein-powder', '-protein', '-powder'];
    this.cards.forEach((card) => {
      const vid = card.dataset.variantId;
      const handle = card.dataset.productHandle;
      if (vid && handle) {
        const cleanHandle = handle.replace(/-sample$/, '');
        this.handleToVariant[cleanHandle] = vid;
        this.variantToHandle[vid] = cleanHandle;

        /* Build normalized key: strip all known suffixes for fuzzy matching */
        let norm = cleanHandle;
        for (const sfx of SUFFIXES_TO_STRIP) {
          if (norm.endsWith(sfx)) {
            norm = norm.slice(0, -sfx.length);
            break;
          }
        }
        this._normalizedHandleMap[norm] = vid;
      }
    });

    /* Build variant-id → flavor-name lookup from data attributes */
    this._variantFlavorName = {};
    this._variantProductId = {};
    this.cards.forEach((card) => {
      const vid = card.dataset.variantId;
      const name = card.dataset.flavorName;
      const pid = card.dataset.productId;
      if (vid && name) this._variantFlavorName[vid] = name;
      if (vid && pid) this._variantProductId[vid] = pid;
    });

    this.restoreFromUrl();

    /* Inline PDP only: move matching flavor to top and preselect qty 1
       when no selections were restored from URL/edit state. */
    this._applyInlinePdpFlavorDefault();

    this._handleSoldOutSelections();
    this.bindEvents();
    if (this.isInline) this._bindPdpAtcIntercept();

    /* Inline PDP only: move the primary group header into the sticky counter
       block so it stays visually locked to it and never scrolls independently. */
    this._anchorPrimaryDividerToSticky();

    /* Suppress the auto-scroll on the initial render so we can do a
       controlled scroll after the page has fully laid out. */
    const restoredComplete = this.totalSelected() >= this.maxSelections;
    if (restoredComplete) {
      this._suppressCompleteScroll = true;
      /* Start in summary view when selections are already complete. */
      this.wasComplete = true;
      this.isCollapsed = true;
    }

    this.render();

    if (restoredComplete) {
      this._suppressCompleteScroll = false;
      /* Delay scroll until after layout fully settles (fan, header, summary) */
      setTimeout(() => {
        /* Force the fan visible on mobile so we account for its height */
        if (this.mobilePreview && window.innerWidth <= 769) {
          this._forceFanVisible = true;
          document.body.classList.add('is-header-sticky');
          this.updateMobilePreviewVisibility();
        }

        /* Use setTimeout again to let the fan render before measuring */
        setTimeout(() => {
          const target = this._getInteractionAnchorTarget();
          if (window.innerWidth > 769) {
            this._scrollToDesktop(target, 24);
          } else {
            const header = document.querySelector('.ae--header');
            const hh = header ? header.offsetHeight : 60;
            const fanH = (this.mobilePreview && this.mobilePreview.classList.contains('is-visible'))
              ? this.mobilePreview.offsetHeight
              : 0;
            const top = target.getBoundingClientRect().top + window.scrollY - hh - fanH - 12;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }, 50);
      }, 50);
    }

    /* If the current page product matches a Quick Pick, mark it active */
    this._activateMatchingQuickPick();

    /* If the URL hash points to this element, scroll it into view after render */
    if (window.location.hash === '#' + this.root.id) {
      this._scrollTo(this.root);
    }
  }

  /* Removes the listeners and observers this instance added outside its own element. */
  destroy() {
    this._abort.abort();
    if (this.mediaObserver) this.mediaObserver.disconnect();
  }

  /* ---- helpers ---- */
  totalSelected() {
    return Object.values(this.selections).reduce((s, q) => s + q, 0);
  }

  remaining() {
    return Math.max(0, this.maxSelections - this.totalSelected());
  }

  remainingToMinimum() {
    return Math.max(0, this.minSelections - this.totalSelected());
  }

  _formatMoney(cents) {
    const value = Number.isFinite(cents) ? cents : 0;
    if (typeof window.formatMoney === 'function') {
      const fmt = window.moneyFormat || '${{amount}}';
      return window.formatMoney(value, fmt);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format((value || 0) / 100);
  }

  _parseMoneyToCents(value) {
    if (value == null) return null;
    const cleaned = String(value)
      .replace(/[^0-9.,-]/g, '')
      .replace(/,/g, '');
    if (!cleaned) return null;
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  }

  _shouldUseDynamicAtcPrice() {
    /* Determine dynamic behavior from the original parent product price,
       not the mutable display price that changes during selection. */
    const originalFromText = this._parseMoneyToCents(this.originalProductPrice);
    if (originalFromText !== null) return originalFromText === 0;
    if (Number.isFinite(this.originalBasePriceCents)) return this.originalBasePriceCents === 0;

    const parsedFromText = this._parseMoneyToCents(this.productPrice);
    if (parsedFromText !== null) return parsedFromText === 0;
    return this.basePriceCents === 0;
  }

  _parentHasNonZeroPrice() {
    const fromOriginalText = this._parseMoneyToCents(this.originalProductPrice);
    if (fromOriginalText !== null) return fromOriginalText > 0;
    if (Number.isFinite(this.originalBasePriceCents)) return this.originalBasePriceCents > 0;
    return Number.isFinite(this.basePriceCents) && this.basePriceCents > 0;
  }

  _totalSelectionAdjustmentCents() {
    let total = 0;
    Object.entries(this.selections).forEach(([vid, qty]) => {
      const item = this.cardData[vid];
      if (!item) return;
      total += (item.priceAdjustmentCents || 0) * qty;
    });
    return total;
  }

  _computeDynamicPriceCents() {
    return this.basePriceCents + this._totalSelectionAdjustmentCents();
  }

  /* Sum the actual unit prices of all selected child products × their quantities. */
  _computeChildrenTotalCents() {
    let total = 0;
    Object.entries(this.selections).forEach(([vid, qty]) => {
      const item = this.cardData[vid];
      if (!item) return;
      total += (item.unitPriceCents || 0) * qty;
    });
    return total;
  }

  _refreshComputedPrice() {
    if (this.isIntegratedPdpByo && this._parentHasNonZeroPrice()) {
      const pinnedFromOriginal = this._parseMoneyToCents(this.originalProductPrice);
      if (pinnedFromOriginal !== null) {
        this.productPrice = this._formatMoney(pinnedFromOriginal);
        return pinnedFromOriginal;
      }
      if (this.originalBasePriceCents > 0) {
        this.productPrice = this._formatMoney(this.originalBasePriceCents);
        return this.originalBasePriceCents;
      }
    }

    if (!this._shouldUseDynamicAtcPrice()) {
      if (this.basePriceCents > 0) {
        this.productPrice = this._formatMoney(this.basePriceCents);
        return this.basePriceCents;
      }
      const parsedFromText = this._parseMoneyToCents(this.productPrice);
      return parsedFromText !== null ? parsedFromText : this.basePriceCents;
    }

    const total = this.totalSelected();
    const computed = total > 0 ? this._computeChildrenTotalCents() : this.basePriceCents;
    this.productPrice = this._formatMoney(computed);
    return computed;
  }

  _numberToWord(n) {
    const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
    if (Number.isInteger(n) && n >= 0 && n < words.length) return words[n];
    return String(n);
  }

  _inlineCategoryMessage(total) {
    const remainingToMin = Math.max(0, this.minSelections - total);
    if (remainingToMin > 0) {
      const remainingWord = this._numberToWord(remainingToMin);
      const stickLabel = remainingToMin === 1 ? 'stick' : 'sticks';
      return `Add ${remainingWord} more ${stickLabel} to unlock (min ${this.minSelections})`;
    }

    const remainingToMax = Math.max(0, this.maxSelections - total);
    if (remainingToMax <= 0) {
      return `<strong>Your ${this.maxSelections}-Pack is ready!</strong>`;
    }

    const stickLabel = remainingToMax === 1 ? 'stick' : 'sticks';
    return `You can add up to ${remainingToMax} more ${stickLabel}`;
  }

  _inlineCategoryMessageHtml(total) {
    const remainingToMin = Math.max(0, this.minSelections - total);
    if (remainingToMin > 0) {
      const remainingWord = this._numberToWord(remainingToMin);
      const stickLabel = remainingToMin === 1 ? 'stick' : 'sticks';
      return `Add <strong>${remainingWord}</strong> more ${stickLabel} to unlock (min ${this.minSelections})`;
    }

    const remainingToMax = Math.max(0, this.maxSelections - total);
    if (remainingToMax > 0) {
      const stickLabel = remainingToMax === 1 ? 'stick' : 'sticks';
      return `You can add up to <strong>${remainingToMax}</strong> more ${stickLabel}`;
    }

    return this._inlineCategoryMessage(total);
  }

  _normalizeFlavorName(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  _getCurrentPdpFlavorName() {
    const candidates = [
      '#selected-flavor',
      '#selected-flavor-hid',
      '.flavor-selected-value .flavor-option-title',
      '.ae--product-flavors .flavor-option.selected .flavor-option-title',
      '.ae--product-title',
    ];

    for (const sel of candidates) {
      const el = document.querySelector(sel);
      const txt = el && el.textContent ? el.textContent.trim() : '';
      if (txt) return txt;
    }

    return '';
  }

  _findMatchingInlineFlavorCard() {
    if (!this.isInline || !this.cards || this.cards.length === 0) return null;

    const pdpFlavor = this._normalizeFlavorName(this._getCurrentPdpFlavorName());
    if (!pdpFlavor) return null;

    let exact = null;
    let fuzzy = null;

    this.cards.forEach((card) => {
      if (card.classList.contains('is-unavailable')) return;
      const cardFlavor = this._normalizeFlavorName(card.dataset.flavorName);
      if (!cardFlavor) return;
      if (!exact && cardFlavor === pdpFlavor) exact = card;
      if (!fuzzy && (cardFlavor.includes(pdpFlavor) || pdpFlavor.includes(cardFlavor))) fuzzy = card;
    });

    return exact || fuzzy;
  }

  _applyGroupVisibility() {
    if (!this.groupToggles || this.groupToggles.length === 0) return;
    this.groupToggles.forEach((toggle) => {
      const group = toggle.dataset.groupToggle;
      const collapsed = toggle.getAttribute('aria-expanded') !== 'true';
      const divider = toggle.closest('.byo-builder__group-divider');
      if (divider) divider.classList.toggle('is-collapsed', collapsed);
      this.cards.forEach((card) => {
        if ((card.dataset.cardGroup || 'primary') !== group) return;
        card.hidden = collapsed;
      });
    });
  }

  _scrollGroupDividerToTop(group) {
    if (!group) return;
    const target = this.root.querySelector(`.byo-builder__group-divider--${group}`);
    if (!target) return;

    const pad = group === 'primary' ? 12 : 8;
    const rootStyle = window.getComputedStyle(this.root);
    const rootIsScrollable = /auto|scroll/.test(rootStyle.overflowY)
      && this.root.scrollHeight > this.root.clientHeight + 1;

    if (rootIsScrollable) {
      if (this.stickyControls && this.stickyControls.contains(target)) {
        this.root.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const rootRect = this.root.getBoundingClientRect();
      const stickyH = this.stickyControls ? this.stickyControls.offsetHeight : 0;
      const top = target.getBoundingClientRect().top - rootRect.top
        + this.root.scrollTop - stickyH - pad;
      this.root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      return;
    }

    if (window.innerWidth > 769) {
      const infoContainer = document.querySelector('.ae--product-information');
      if (infoContainer) {
        const containerRect = infoContainer.getBoundingClientRect();
        const top = target.getBoundingClientRect().top - containerRect.top
          + infoContainer.scrollTop - pad;
        infoContainer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
      }
    }

    const header = document.querySelector('.ae--header');
    const headerH = header ? header.offsetHeight : 60;
    const fanH = this.mobilePreview && this.mobilePreview.classList.contains('is-visible')
      ? this.mobilePreview.offsetHeight : 0;
    const stickyH = this.stickyControls ? this.stickyControls.offsetHeight : 0;
    const top = target.getBoundingClientRect().top + window.scrollY
      - headerH - fanH - stickyH - pad;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  _observeSummaryResize() {
    if (!this.summaryItems) return;
    if (this._summaryResizeObserver || typeof ResizeObserver === 'undefined') return;
    let lastW = this.summaryItems.clientWidth;
    this._summaryResizeObserver = new ResizeObserver(() => {
      const w = this.summaryItems.clientWidth;
      if (w !== lastW) {
        lastW = w;
        this.renderSummary();
      }
    });
    this._summaryResizeObserver.observe(this.summaryItems);
  }

  _bindSummaryDragScroll() {
    if (!this.summaryItems) return;
    if (this.summaryItems.dataset.byoDragBound === '1') return;
    this.summaryItems.dataset.byoDragBound = '1';

    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    const el = this.summaryItems;

    const onDown = (e) => {
      isDown = true;
      moved = false;
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      startScroll = el.scrollLeft;
      el.classList.add('is-dragging');
    };
    const onMove = (e) => {
      if (!isDown) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const dx = x - startX;
      if (Math.abs(dx) > 3) moved = true;
      el.scrollLeft = startScroll - dx;
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('is-dragging');
      /* Suppress the post-drag click that would otherwise re-open the builder. */
      if (moved) {
        const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        el.addEventListener('click', swallow, { capture: true, once: true });
      }
    };

    el.addEventListener('mousedown', onDown);
    el.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    el.addEventListener('mouseleave', onUp);
    el.addEventListener('touchstart', onDown, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onUp);
    el.addEventListener('touchcancel', onUp);
  }

  _updateInlineListMaxHeight() {
    if (!this.isIntegratedPdpByo) return;
    const target = this.root.classList.contains('byo-builder--list') ? this.root : null;
    /* Clear any legacy inline cap on the inner list. */
    if (this.flavorList) this.flavorList.style.maxHeight = '';
    if (!target) return;

    const isMobile = window.matchMedia('(max-width: 769px)').matches;
    if (!isMobile) {
      target.style.maxHeight = '';
      target.style.overflowY = '';
      target.style.removeProperty('--byo-panel-offset');
      this._inlineListSized = false;
      return;
    }

    /* Skip while the BYO wrapper is hidden — measurements would be stale. */
    const wrapper = this.root.closest('.pdp-ss__byo-inline');
    if (wrapper && window.getComputedStyle(wrapper).display === 'none') {
      return;
    }

    /* Stable budget: assume the sticky-header state (worst case) so the size
       doesn't jump between scroll positions. Sum the fixed layout above the
       panel (page header + anchor pad + size-selector row) and below it
       (ATC + pad). All values come from element offsetHeight, not rect.top,
       so this is independent of scroll. CSS uses `svh` for the viewport so
       browser-chrome show/hide can't change it either. */
    const pageHeader = document.querySelector('.ae--header');
    const selectSize = document.querySelector('.select-a-size');
    const stickyAtc = document.querySelector('.pdp-atc-wrap');
    const headerH = pageHeader ? pageHeader.offsetHeight : 60;
    const anchorPad = 12;
    const selectSizeH = selectSize ? selectSize.offsetHeight : 0;
    const atcH = stickyAtc ? stickyAtc.offsetHeight : 0;
    const bottomPad = 16;
    const offset = headerH + anchorPad + selectSizeH + atcH + bottomPad;
    this._inlineListRetries = 0;
    target.style.setProperty('--byo-panel-offset', offset + 'px');
    target.style.overflowY = 'auto';
    this._inlineListSized = true;
  }

  _moveCardToTop(card) {
    if (!card || !this.flavorList) return;

    /* Sit directly under the card's own group header, not above it. */
    const group = card.dataset.cardGroup || 'primary';
    const divider = this.flavorList.querySelector('.byo-builder__group-divider--' + group);
    const anchor = divider ? divider.nextElementSibling : this.flavorList.firstElementChild;
    if (anchor !== card) {
      this.flavorList.insertBefore(card, anchor);
    }

    this.cards = [card].concat(this.cards.filter((c) => c !== card));
  }

  _applyInlinePdpFlavorDefault() {
    if (!this.isInline) return;

    const matchedCard = this._findMatchingInlineFlavorCard();
    if (!matchedCard) return;

    this._moveCardToTop(matchedCard);
    this._inlinePinnedFlavorVid = matchedCard.dataset.variantId || null;

    /* Only set default qty when user is not restoring an existing BYO state */
    if (this._editByoId || this.totalSelected() > 0) return;

    const vid = matchedCard.dataset.variantId;
    if (!vid || this.unavailableVids.has(vid)) return;

    this.selections[vid] = 1;
  }

  _findCardByVariantId(variantId) {
    if (!variantId) return null;
    return this.cards.find((card) => card.dataset.variantId === String(variantId)) || null;
  }

  _firstAvailableCard() {
    return this.cards.find((card) => !card.classList.contains('is-unavailable')) || null;
  }

  _resolveInlineFlavorCardForClear() {
    let card = this._findMatchingInlineFlavorCard();
    if (card) return card;

    card = this._findCardByVariantId(this._inlinePinnedFlavorVid);
    if (card && !card.classList.contains('is-unavailable')) return card;

    return this._firstAvailableCard();
  }

  _preserveInlinePdpFlavorAfterClear() {
    if (!this.isInline) return;

    const matchedCard = this._resolveInlineFlavorCardForClear();
    if (!matchedCard) return;

    this._moveCardToTop(matchedCard);
    this._inlinePinnedFlavorVid = matchedCard.dataset.variantId || this._inlinePinnedFlavorVid;

    const vid = matchedCard.dataset.variantId;
    if (!vid || this.unavailableVids.has(vid)) return;

    this.selections[vid] = 1;
  }

  /** Inline BYO interactions anchor to the builder panel itself. Standalone
   *  builders retain the Select a Size/header fallback used previously. */
  _getInteractionAnchorTarget() {
    if (this.isInline) {
      const inlineWrapper = this.root.closest('.pdp-ss__byo-inline');
      if (inlineWrapper) return inlineWrapper;
    }
    return document.querySelector('.select-a-size') || this.builderHeader || this.root;
  }

  /** Smooth-scroll to an element, offset by the sticky header height,
   *  the fixed mobile-preview fan (when visible), and an optional pad. */
  _scrollTo(el, pad = 16) {
    requestAnimationFrame(() => {
      /* Force header sticky since we're scrolling — it will become fixed anyway */
      document.body.classList.add('is-header-sticky');
      const header = document.querySelector('.ae--header');
      const hh = header ? header.offsetHeight : 60;
      const fanH = (this.mobilePreview && this.mobilePreview.classList.contains('is-visible'))
        ? this.mobilePreview.offsetHeight
        : 0;
      const top = el.getBoundingClientRect().top + window.scrollY - hh - fanH - pad;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  /**
   * On desktop, scroll within the .ae--product-information container.
   * Also scrolls the page if needed so the target isn't hidden under the sticky header.
   */
  _scrollToDesktop(el, pad = 16) {
    const infoContainer = document.querySelector('.ae--product-information');
    if (!infoContainer) {
      this._scrollTo(el, pad);
      return;
    }

    requestAnimationFrame(() => {
      const header = document.querySelector('.ae--header');
      const headerHeight = header ? header.offsetHeight : 60;

      /* First, ensure the page is scrolled so the info container top 
         is visible below the sticky header */
      const containerRect = infoContainer.getBoundingClientRect();
      if (containerRect.top < headerHeight) {
        /* Container is partially behind header, scroll page to fix */
        const pageScrollTarget = infoContainer.getBoundingClientRect().top + window.scrollY - headerHeight - pad;
        window.scrollTo({ top: pageScrollTarget, behavior: 'smooth' });
      }

      /* Now scroll within the container to show the target element */
      const targetRect = el.getBoundingClientRect();
      const scrollOffset = targetRect.top - containerRect.top + infoContainer.scrollTop;
      infoContainer.scrollTo({ top: scrollOffset, behavior: 'smooth' });
    });
  }

  /** Anchor the viewport just above the sticky BYO controls block. */
  _anchorAboveSticky() {
    const target = this.stickyControls || this.builderHeader || this.root;
    if (window.innerWidth > 769) {
      this._scrollToDesktop(target, 24);
    } else {
      this._scrollTo(target, 4);
    }
  }

  /**
   * On mobile, force the mobile preview visible when flavors are added/removed
   * and scroll to anchor the builder controls into view.
   */
  _showMobilePreviewAndAnchor() {
    if (this.embedded) { this.updateMobilePreviewVisibility(); return; }
    if (!this.mobilePreview || window.innerWidth > 769) return;
    if (this.totalSelected() > 0) {
      const wasVisible = this.mobilePreview.classList.contains('is-visible');
      this._forceFanVisible = true;
      this.updateMobilePreviewVisibility();
      /* Only scroll when the preview just appeared or the builder is off-screen */
      if (!wasVisible) {
        requestAnimationFrame(() => {
          this._anchorAboveSticky();
        });
      }
    }
  }

  _anchorToSelectASize() {
    if (this.embedded) return;
    if (!window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return;
    const el = this._getInteractionAnchorTarget();
    if (!el) return;
    requestAnimationFrame(() => {
      document.body.classList.add('is-header-sticky');
      const hdr = document.querySelector('.ae--header');
      const stickyOffset = hdr ? (hdr.offsetHeight || 0) : 60;
      const fanOffset = (this.mobilePreview && this.mobilePreview.classList.contains('is-visible'))
        ? this.mobilePreview.offsetHeight
        : 0;
      const top = el.getBoundingClientRect().top + window.pageYOffset - stickyOffset - fanOffset - 12;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'auto' });
    });
  }

  /**
   * Resolve a product handle to a variant ID.
   * Tries exact match first, then strips known suffixes for fuzzy matching,
   * then tries substring containment as a last resort.
   */
  _resolveHandle(handle) {
    /* 1. Exact match on cleaned card handles */
    if (this.handleToVariant[handle]) return this.handleToVariant[handle];

    /* 2. Strip known product-type suffixes from the preset handle and try normalized map */
    const SUFFIXES = ['-protein-powder', '-protein', '-powder'];
    let norm = handle;
    for (const sfx of SUFFIXES) {
      if (norm.endsWith(sfx)) { norm = norm.slice(0, -sfx.length); break; }
    }
    if (this._normalizedHandleMap[norm]) return this._normalizedHandleMap[norm];

    /* 3. Substring containment: check if preset handle contains or is contained by a card handle */
    for (const [cardHandle, vid] of Object.entries(this.handleToVariant)) {
      if (cardHandle.includes(handle) || handle.includes(cardHandle)) return vid;
    }

    /* 4. Normalized substring containment */
    for (const [normHandle, vid] of Object.entries(this._normalizedHandleMap)) {
      if (normHandle.includes(norm) || norm.includes(normHandle)) return vid;
    }

    return undefined;
  }

  /**
   * Bind a long-press-to-repeat handler on a button.
   * Initial delay: 400ms, repeat: 200ms accelerating down to 80ms.
   */
  _bindLongPress(btn, action) {
    const INITIAL_DELAY = 400;
    const REPEAT_START = 200;
    const REPEAT_MIN = 80;
    const ACCEL = 0.7;

    btn.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      let timer;
      let interval = REPEAT_START;

      const repeat = () => {
        action();
        interval = Math.max(REPEAT_MIN, interval * ACCEL);
        timer = window.setTimeout(repeat, interval);
      };

      const initialTimer = window.setTimeout(() => {
        this._longPressActive = true;
        repeat();
      }, INITIAL_DELAY);

      const cleanup = () => {
        clearTimeout(initialTimer);
        clearTimeout(timer);
        this._longPressActive = false;
        window.removeEventListener('pointerup', cleanup);
        window.removeEventListener('pointercancel', cleanup);
      };

      window.addEventListener('pointerup', cleanup);
      window.addEventListener('pointercancel', cleanup);
    });
  }

  /** Compute sticky top offsets so the preview + controls sit below the fixed header */
  _anchorPrimaryDividerToSticky() {
    if (!this.isInline || !this.stickyControls) return;
    const primaryDivider = this.root.querySelector('.byo-builder__group-divider--primary');
    if (!primaryDivider) return;
    /* Append the divider into the sticky counter block so it moves with it
       instead of pinning independently. */
    if (primaryDivider.parentElement !== this.stickyControls) {
      this.stickyControls.appendChild(primaryDivider);
      primaryDivider.classList.add('byo-builder__group-divider--in-sticky');
    }
  }

  _updateStickyOffsets() {
    if (this.embedded) return;
    const header = document.querySelector('.ae--header');
    const hh = header ? header.offsetHeight : 60;

    if (this.mobilePreview) {
      this.mobilePreview.style.top = hh + 'px';
    }

    /* Controls stick directly below the header + fan (preview is fixed, not in flow)
       Skip if summary is visible - we don't want sticky behavior then */
    if (this.stickyControls && !this.stickyControls.classList.contains('byo-sticky--summary-visible')) {
      const fanH = (this.mobilePreview && this.mobilePreview.classList.contains('is-visible'))
        ? this.mobilePreview.offsetHeight
        : 0;
      this.stickyControls.style.top = (hh + fanH - 1) + 'px';
    }

    /* Publish the sticky counter's height so the expanded group divider can
       stack directly below it via CSS `top: var(--byo-sticky-h)`. */
    if (this.stickyControls) {
      const stickyH = this.stickyControls.offsetHeight;
      this.root.style.setProperty('--byo-sticky-h', stickyH + 'px');
    }
  }

  /**
   * On mobile, stop the sticky controls from covering the last flavor card.
   * Uses a scroll listener to toggle `.byo-sticky--unstuck` when the sticky
   * element would overlap the last visible card.
   * Note: Summary visibility is handled separately via `.byo-sticky--summary-visible`.
   */
  _bindStickyUnstickListener() {
    if (this.embedded) return;
    let ticking = false;

    const checkStickyPosition = () => {
      ticking = false;
      if (this.embedded) return;
      if (window.innerWidth > 769) return; // Mobile only

      if (!this.stickyControls) return;

      /* Skip check if summary is visible (handled by separate class) */
      if (this.stickyControls.classList.contains('byo-sticky--summary-visible')) {
        this.stickyControls.classList.remove('byo-sticky--unstuck');
        return;
      }

      const header = document.querySelector('.ae--header');
      const headerHeight = header ? header.offsetHeight : 60;
      const fanHeight = (this.mobilePreview && this.mobilePreview.classList.contains('is-visible'))
        ? this.mobilePreview.offsetHeight
        : 0;
      const stickyHeight = this.stickyControls.offsetHeight;
      const stickyBottom = headerHeight + fanHeight + stickyHeight;

      /* Check the last visible card in the flavor list */
      const lastCard = this.flavorList ? this.flavorList.querySelector('.byo-card:last-child') : null;

      if (!lastCard) return;

      const lastCardRect = lastCard.getBoundingClientRect();

      /* Unstick when the last card's top would be above or at the sticky bottom */
      const shouldUnstick = lastCardRect.top <= stickyBottom + 8; // 8px buffer

      this.stickyControls.classList.toggle('byo-sticky--unstuck', shouldUnstick);
    };

    /* Store reference so we can call it from render() */
    this._checkStickyPosition = checkStickyPosition;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(checkStickyPosition);
        ticking = true;
      }
    }, { passive: true });

    /* Initial check */
    checkStickyPosition();
  }

  /** Show mobile preview only when there are selections AND the media is scrolled out of view,
   *  OR when a Quick Pick has force-shown the fan (_forceFanVisible). */
  updateMobilePreviewVisibility() {
    if (!this.mobilePreview) return;
    const hasSelections = this.totalSelected() > 0;
    if (!hasSelections) this._forceFanVisible = false;

    if (this.embedded) {
      this.mobilePreview.classList.toggle('is-visible', hasSelections);
      this.mobilePreview.setAttribute('aria-hidden', hasSelections ? 'false' : 'true');
      return;
    }
    const shouldShow = hasSelections && (this._forceFanVisible || this.mediaOutOfView);
    this.mobilePreview.classList.toggle('is-visible', shouldShow);
    this.mobilePreview.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

    /* Force the sticky header to stay visible while the fan preview is showing,
       so the fixed fan always sits directly below the header. */
    if (shouldShow) {
      document.body.classList.add('is-header-sticky');
    }

    /* Hide the fake-scrollbar whenever the fan preview or byo-fan-active is shown */
    const fakeScrollbar = document.getElementById('fakeScrollbar');
    if (fakeScrollbar) {
      const fanActive = shouldShow || (this.productMedia && this.productMedia.classList.contains('byo-fan-active'));
      fakeScrollbar.style.display = fanActive ? 'none' : '';
    }

    this._updateStickyOffsets();
  }

  /* ---- Inline BYO: mirror state to the regular PDP ATC button ---- */
  _updatePdpAtcButton(isComplete) {
    if (!this.isInline) return;
    /* Only update when the BYO inline wrapper is actually visible */
    var wrapper = this.root.closest('.pdp-ss__byo-inline');
    if (!wrapper || window.getComputedStyle(wrapper).display === 'none') return;

    var btn = document.querySelector('.pdp-atc-btn');
    if (!btn) return;

    var labelEl = btn.querySelector('.pdp-atc-btn__label');
    var priceEl = btn.querySelector('[data-atc-price]');
    var compareEl = btn.querySelector('[data-atc-compare]');

    if (isComplete) {
      btn.disabled = false;
      if (labelEl) labelEl.textContent = this._editByoId ? 'Update' : 'Add to Cart';
      if (priceEl) priceEl.textContent = this.productPrice;
      if (compareEl) compareEl.style.display = 'none';
    } else {
      btn.disabled = true;
      const total = this.totalSelected();
      if (labelEl) labelEl.textContent = total === 0
        ? 'Select up to ' + this.maxSelections
        : 'Select ' + this.remainingToMinimum() + ' more';
      if (priceEl) priceEl.textContent = this.productPrice;
      if (compareEl) compareEl.style.display = 'none';
    }
  }

  /* ---- Inline BYO: intercept PDP ATC click to call BYO addToCart ---- */
  _bindPdpAtcIntercept() {
    var self = this;
    this._pdpAtcButton = document.querySelector('.pdp-atc-btn');
    if (!this._pdpAtcButton) return;

    this._pdpAtcHandler = function (e) {
      /* Only intercept when this inline BYO wrapper is actually visible */
      var wrapper = self.root.closest('.pdp-ss__byo-inline');
      if (!wrapper || window.getComputedStyle(wrapper).display === 'none') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      self.addToCart();
    };

    this._pdpAtcButton.addEventListener('click', this._pdpAtcHandler, { capture: true });
  }

  /* ---- dataLayer tracking ---- */
  _trackByo(data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'byo_interaction', ...data });
  }

  _flavorName(vid) {
    return this._variantFlavorName[vid] || this.variantToHandle[vid] || vid;
  }

  /* ---- event binding ---- */
  bindEvents() {
    if (this.root.dataset.byoBound === '1') return;
    this.root.dataset.byoBound = '1';

    const SWIPE_THRESHOLD = 30;
    const LONG_PRESS_DELAY = 400;
    const REPEAT_INTERVAL = 200;
    const MIN_REPEAT = 80;
    const ACCEL_FACTOR = 0.7;

    /* Group accordion headers (inline PDP builder) — exactly one open at a time. */
    this.groupToggles = Array.from(this.root.querySelectorAll('[data-group-toggle]'));
    this.root.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-group-toggle]');
      if (!toggle || !this.root.contains(toggle)) return;
      e.preventDefault();
      e.stopPropagation();
      const wasExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const currentIndex = this.groupToggles.indexOf(toggle);
      const nextToggle = wasExpanded && currentIndex !== -1
        ? this.groupToggles[(currentIndex + 1) % this.groupToggles.length]
        : toggle;
      this.groupToggles.forEach((t) => {
        t.setAttribute('aria-expanded', t === nextToggle ? 'true' : 'false');
      });
      this._applyGroupVisibility();
      requestAnimationFrame(() => {
        this._scrollGroupDividerToTop(nextToggle.dataset.groupToggle);
      });
    });
    this._applyGroupVisibility();

    /* Mobile inline BYO: cap the builder to fit above the sticky ATC. Recompute
       only on resize, state change (via render), and when the panel is made
       visible — not on scroll, so scrolling past can't grow the container. */
    if (this.isIntegratedPdpByo && this.flavorList) {
      this._updateInlineListMaxHeight = this._updateInlineListMaxHeight.bind(this);
      window.addEventListener('resize', this._updateInlineListMaxHeight, { signal: this._abort.signal });
      document.addEventListener('pdp:layout-changed', () => {
        this._updateInlineListMaxHeight();
      }, { signal: this._abort.signal });
      /* IntersectionObserver as belt-and-braces: recompute whenever the wrapper
         actually enters the viewport, in case layout-changed missed a frame. */
      const wrapper = this.root.closest('.pdp-ss__byo-inline');
      if (wrapper && typeof IntersectionObserver !== 'undefined') {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this._updateInlineListMaxHeight();
          });
        }, { threshold: 0.01 });
        io.observe(wrapper);
      }
      requestAnimationFrame(this._updateInlineListMaxHeight);
    }

    /* Category group labels → active indicator + scroll to matching list divider */
    const categoryGroupEls = Array.from(this.root.querySelectorAll('.byo-builder__category-group'));
    const setActiveCategoryGroup = (activeIdx) => {
      categoryGroupEls.forEach((g, idx) => g.classList.toggle('is-active', idx === activeIdx));
    };

    const syncCategoryGroupFromScroll = () => {
      if (window.innerWidth > 769) return;
      if (this.isCollapsed) return;
      if (categoryGroupEls.length < 2) return;

      const halfViewport = window.innerHeight * 0.5;
      let activeIdx = 0;
      categoryGroupEls.forEach((groupEl, idx) => {
        const group = groupEl.dataset.categoryGroup || (idx === 0 ? 'primary' : 'secondary');
        const divider = this.root.querySelector(`.byo-builder__group-divider--${group}`);
        if (divider && divider.getBoundingClientRect().top <= halfViewport) activeIdx = idx;
      });
      setActiveCategoryGroup(activeIdx);
    };

    /* Primary group is active by default */
    if (categoryGroupEls.length > 0) setActiveCategoryGroup(0);

    if (categoryGroupEls.length > 1) {
      let groupScrollTicking = false;
      const onGroupScrollSync = () => {
        if (groupScrollTicking) return;
        groupScrollTicking = true;
        requestAnimationFrame(() => {
          groupScrollTicking = false;
          syncCategoryGroupFromScroll();
        });
      };

      window.addEventListener('scroll', onGroupScrollSync, { passive: true, signal: this._abort.signal });
      window.addEventListener('resize', onGroupScrollSync, { signal: this._abort.signal });
      requestAnimationFrame(syncCategoryGroupFromScroll);
    }

    categoryGroupEls.forEach((groupEl, idx) => {
      groupEl.style.cursor = 'pointer';
      groupEl.addEventListener('click', () => {
        setActiveCategoryGroup(idx);
        const group = groupEl.dataset.categoryGroup || (idx === 0 ? 'primary' : 'secondary');
        const scrollToGroupDivider = () => {
          this._scrollGroupDividerToTop(group);
        };

        if (this.isCollapsed) {
          this.isCollapsed = false;
          this.render();
          requestAnimationFrame(scrollToGroupDivider);
          return;
        }

        scrollToGroupDivider();
      });
    });

    this.cards.forEach((card) => {
      const vid = card.dataset.variantId;
      if (card.classList.contains('is-unavailable')) return;

      /* initial add (the "+" circle on the card) */
      const addBtn = card.querySelector('.byo-card__add-btn');
      if (addBtn) {
        addBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.increment(vid);
        });
        this._bindLongPress(addBtn, () => this.increment(vid, 'long press'));
      }

      /* card body tap also adds */
      card.addEventListener('click', (e) => {
        if (e.target.closest('.byo-card__qty')) return; // let qty controls handle
        if (this.embedded) return; // routine sheet: only the + button adds
        this.increment(vid);
      });

      /* qty +/- buttons */
      const incBtn = card.querySelector('[data-action="inc"]');
      const decBtn = card.querySelector('[data-action="dec"]');
      if (incBtn) {
        incBtn.addEventListener('click', (e) => { e.stopPropagation(); this.increment(vid); });
        this._bindLongPress(incBtn, () => this.increment(vid, 'long press'));
      }
      if (decBtn) {
        decBtn.addEventListener('click', (e) => { e.stopPropagation(); this.decrement(vid); });
        this._bindLongPress(decBtn, () => this.decrement(vid, 'long press'));
      }

      /* Swipe-to-increment/decrement on touch */
      card.addEventListener('touchstart', (e) => {
        if (card.classList.contains('is-unavailable')) return;
        if (this.embedded) return; // routine sheet: swipe does not add or remove
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;
        let fired = false;
        let dirDetected = false;
        let isHorizontal = false;

        const el = e.currentTarget;

        const onMove = (ev) => {
          if (fired) return;
          const t = ev.touches[0];
          const dx = t.clientX - startX;
          const dy = t.clientY - startY;

          if (!dirDetected) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            if (Math.abs(dy) > Math.abs(dx)) {
              el.removeEventListener('touchmove', onMove);
              return;
            }
            dirDetected = true;
            isHorizontal = true;
          }

          if (isHorizontal) ev.preventDefault();

          if (dx > SWIPE_THRESHOLD && this.totalSelected() < this.maxSelections) {
            fired = true;
            this.increment(vid, 'swipe');
          } else if (dx < -SWIPE_THRESHOLD && (this.selections[vid] || 0) > 0) {
            fired = true;
            this.decrement(vid, 'swipe');
          }
        };

        const cleanup = () => {
          el.removeEventListener('touchmove', onMove);
          el.removeEventListener('touchend', cleanup);
          el.removeEventListener('touchcancel', cleanup);
        };

        el.addEventListener('touchmove', onMove, { passive: false });
        el.addEventListener('touchend', cleanup);
        el.addEventListener('touchcancel', cleanup);
      });
    });

    /* ATC */
    this.atcButton.addEventListener('click', () => this.addToCart());

    /* Bundle quantity +/-
       Bind directly to qty buttons and de-duplicate synthetic follow-up events
       (e.g. pointerdown -> click) so each press changes quantity exactly once. */
    const qtyButtons = Array.from(this.root.querySelectorAll('[data-bundle-qty]'));
    const activateQty = (btn, e) => {
      if (!btn) return;

      var now = Date.now();
      var last = parseInt(btn.dataset.byoLastTs || '0', 10);
      if (!Number.isNaN(last) && now - last < 250) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      btn.dataset.byoLastTs = String(now);

      e.preventDefault();
      e.stopImmediatePropagation();

      const dir = btn.dataset.bundleQty;
      if (dir === 'inc') {
        this.setBundleQty(this.bundleQty + 1);
      } else if (dir === 'dec') {
        this.setBundleQty(this.bundleQty - 1);
      }

    };

    qtyButtons.forEach((btn) => {
      ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach((evt) => {
        btn.addEventListener(evt, (e) => activateQty(btn, e), { capture: true, passive: false });
      });
    });

    /* Edit Selections (expand back from summary) */
    if (this.editBtn) {
      this.editBtn.addEventListener('click', () => {
        this.isCollapsed = false;
        this.render();
        this._anchorAboveSticky();
      });
    }

    /* Clear button – reset all selections */
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this._trackByo({
          interaction_type: 'click',
          element_name: 'menu',
          option_selected: 'clear',
        });

        const isPreset = !!this.root.dataset.preselections;
        const clearUrl = this.root.dataset.clearUrl;

        if (isPreset && clearUrl) {
          /* Preset product → navigate to the BYO product page */
          window.location.href = clearUrl + '#byo-builder';
        } else {
          /* BYO product → clear in-place */
          this.selections = {};
          this.isCollapsed = false;
          this.wasComplete = false;
          this._forceFanVisible = false;
          this._dismissOosAlert();
          this._clearActiveQuickPick();
          this.setBundleQty(1);
          /* Re-apply the page flavor default so the current flavor stays selected */
          this._preserveInlinePdpFlavorAfterClear();
          this.render();
          
          /* Anchor back to the inline BYO panel (or standalone size area). */
          const target = this._getInteractionAnchorTarget();
          if (window.innerWidth > 769) {
            this._scrollToDesktop(target, 24);
          } else {
            this._scrollTo(target, 12);
          }
        }
      });
    }

    /* Show / Hide toggle – toggle collapsed state */
    if (this.showBtn) {
      this.showBtn.addEventListener('click', () => {
        this.isCollapsed = !this.isCollapsed;
        this._trackByo({
          interaction_type: 'click',
          element_name: 'protein powder',
          option_selected: this.isCollapsed ? 'hide' : 'show',
        });
        this.render();
        this._anchorIfAboveBuilder();
      });
    }

    /* Clicking a summary chip also expands (same as Show) and scrolls to top */
    if (this.summaryContainer) {
      this.summaryContainer.addEventListener('click', (e) => {
        if (e.target.closest('.byo-summary__chip')) {
          this.isCollapsed = !this.isCollapsed;
          this.render();
          this._anchorAboveSticky();
        }
      });
    }

    /* Quick Pick buttons – apply presets instantly */
    this.quickPicks.forEach((btn) => {
      btn.addEventListener('click', () => {
        const qpLabel = (btn.querySelector('.byo-quick-pick__title') || {}).textContent || '';
        this._trackByo({
          interaction_type: 'click',
          element_name: 'quickpick',
          option_selected: qpLabel.trim(),
        });

        const raw = btn.dataset.presets;
        if (!raw) return;

        let items;
        try { items = JSON.parse(raw); } catch (e) { return; }
        if (!Array.isArray(items) || items.length === 0) return;

        /* Clear current selections */
        this.selections = {};
        this.isCollapsed = false;
        this.wasComplete = false;
        this._dismissOosAlert();

        /* Apply the preset */
        let total = 0;
        items.forEach(({ handle, quantity }) => {
          const vid = this._resolveHandle(handle);
          const qty = parseInt(quantity, 10);
          if (!vid || !qty || qty <= 0) return;
          const allowed = Math.min(qty, this.maxSelections - total);
          if (allowed <= 0) return;
          this.selections[vid] = (this.selections[vid] || 0) + allowed;
          total += allowed;
        });

        /* Remove any sold-out items and show a warning if needed */
        this._handleSoldOutSelections();

        /* Update active state on Quick Pick buttons */
        this.quickPicks.forEach((qp) => qp.classList.remove('is-active'));
        btn.classList.add('is-active');

        /* Swap parent variant to the Quick Pick's product so ATC
           submits the correct bundle product, not the page product. */
        const qpVariant = parseInt(btn.dataset.variantId, 10);
        const qpPrice = btn.dataset.productPrice || '';
        const qpBasePriceCents = parseInt(btn.dataset.productPriceCents, 10);
        const qpTitle = btn.dataset.productTitle || '';
        const qpUrl = btn.dataset.productUrl || '';
        if (qpVariant) {
          this.parentVariantId = qpVariant;
          this.productPrice = qpPrice;
          if (!Number.isNaN(qpBasePriceCents)) {
            this.basePriceCents = qpBasePriceCents;
          }
        }

        /* Update page title, price, and browser URL to reflect the QP product */
        //this._updatePageProduct(qpTitle, qpPrice, qpUrl);

        this.activeQuickPick = btn;
        this._suppressCompleteScroll = true;

        /* On mobile, force the sticky fan visible so the user sees
           both the fan and the Quick Picks header anchored below it. */
        if (this.mobilePreview && window.innerWidth <= 769) {
          this._forceFanVisible = true;
          document.body.classList.add('is-header-sticky');
        }

        this.render();
        this._suppressCompleteScroll = false;

        /* Smooth-scroll so the Quick Picks header sits right below
           the sticky header + fan preview. */
        const qpSection = this.root.querySelector('.byo-quick-picks');
        if (qpSection) {
          if (window.innerWidth > 769) {
            this._scrollToDesktop(qpSection);
          } else {
            this._scrollTo(qpSection);
          }
        }
      });
    });

    /* Description "more" link – open the description pane and scroll to it */
    const moreLink = this.root.querySelector('[data-scroll-to-desc]');
    if (moreLink) {
      moreLink.addEventListener('click', (e) => {
        e.preventDefault();
        /* Mobile uses <details>, desktop uses a plain div */
        const mobileDesc = document.getElementById('byo-product-description');
        const desktopDesc = document.getElementById('byo-product-description-desktop');

        /* Pick whichever is currently visible */
        const target = desktopDesc && desktopDesc.offsetParent !== null
          ? desktopDesc
          : mobileDesc;

        if (target) {
          /* Open the <details> if it's the mobile accordion */
          if (target.tagName === 'DETAILS' && !target.open) target.open = true;

          /* On desktop, scroll within the .ae--product-information container */
          if (window.innerWidth > 769) {
            this._scrollToDesktop(target);
          } else {
            /* On mobile, force the sticky fan + header visible before scrolling
               so _scrollTo accounts for their full height immediately. */
            if (this.mobilePreview && this.totalSelected() > 0 && window.innerWidth <= 769) {
              this._forceFanVisible = true;
              document.body.classList.add('is-header-sticky');
              this.updateMobilePreviewVisibility();
            }

            this._scrollTo(target);
          }
        }
      });
    }
  }

  setBundleQty(val) {
    this.bundleQty = Math.max(1, val);
    if (this.bundleQtyInput) this.bundleQtyInput.value = this.bundleQty;
    this.pushToUrl();
  }


  /* ---- URL sync ---- */

  /**
   * Read URL search params and restore selections + bundle qty.
   * Falls back to metafield-based preselections if no URL flavors are present.
   * Format: ?flavors=handle:qty,handle:qty&bundle-qty=N
   */
  restoreFromUrl() {
    if (this.embedded) return;
    const params = new URLSearchParams(window.location.search);

    /* Bundle quantity */
    const bqParam = parseInt(params.get('bundle-qty'), 10);
    if (bqParam > 0) this.setBundleQty(bqParam);

    /* If we arrived via an "Update" link from the cart, capture the
       _byo_id of the bundle we're editing so addToCart can replace it. */
    const editId = params.get('edit-byo-id');
    if (editId) this._editByoId = editId;

    /* Flavor selections from URL */
    const flavorsParam = params.get('flavors');
    if (flavorsParam) {
      this._applyFlavorsString(flavorsParam);
      return;
    }

    /* No URL flavors → try preselections from bundle_configuration metafield */
    this._applyPreselections();
  }

  /**
   * Parse a "handle:qty,handle:qty" string and apply to selections.
   */
  _applyFlavorsString(flavorsParam) {
    let totalRestored = 0;
    flavorsParam.split(',').forEach((entry) => {
      const [handle, qtyStr] = entry.split(':');
      const qty = parseInt(qtyStr, 10);
      const vid = this._resolveHandle(handle);
      if (!vid || !qty || qty <= 0) return;

      /* Cap to max remaining */
      const allowed = Math.min(qty, this.maxSelections - totalRestored);
      if (allowed <= 0) return;

      this.selections[vid] = allowed;
      totalRestored += allowed;
    });
  }

  /**
   * Apply preselections from the data-preselections attribute
   * (sourced from the cleansimpleeats.bundle_configuration metafield).
   */
  _applyPreselections() {
    const raw = this.root.dataset.preselections;
    if (!raw) return;

    let items;
    try { items = JSON.parse(raw); } catch { return; }
    if (!Array.isArray(items) || items.length === 0) return;

    let totalRestored = 0;
    items.forEach(({ handle, quantity }) => {
      const vid = this._resolveHandle(handle);
      const qty = parseInt(quantity, 10);
      if (!vid || !qty || qty <= 0) return;

      const allowed = Math.min(qty, this.maxSelections - totalRestored);
      if (allowed <= 0) return;

      this.selections[vid] = (this.selections[vid] || 0) + allowed;
      totalRestored += allowed;
    });
  }

  /**
   * After preselections / URL restore, check for any selected items that
   * are actually sold out. Remove them from selections and display a
   * warning banner listing the sold-out flavors + how many more to pick.
   */
  _handleSoldOutSelections() {
    if (this.unavailableVids.size === 0) return;

    const removedNames = [];
    let removedQty = 0;

    for (const vid of this.unavailableVids) {
      if (this.selections[vid]) {
        removedQty += this.selections[vid];
        removedNames.push(this.cardData[vid]?.title || 'Unknown');
        delete this.selections[vid];
      }
    }

    if (removedNames.length === 0) return;

    /* Build the alert message */
    const nameList = removedNames
      .map((n) => `<strong>${n.toUpperCase()}</strong>`)
      .join(', ');
    const moreNeeded = removedQty;
    const plural = removedNames.length > 1 ? 'are' : 'is';
    const msg = `${nameList} ${plural} sold out. Select ${moreNeeded} more to complete your pack.`;

    if (this.oosAlert) {
      this.oosAlert.innerHTML = `
        <svg class="byo-builder__oos-icon" viewBox="0 0 20 20" width="18" height="18" fill="none">
          <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.5"/>
          <path d="M10 6v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="10" cy="14" r="1" fill="currentColor"/>
        </svg>
        <span>${msg}</span>`;
      this.oosAlert.style.display = '';

      /* Re-check sticky position since OOS alert changes sticky height */
      if (this._checkStickyPosition) {
        requestAnimationFrame(() => this._checkStickyPosition());
      }
    }
  }

  /** Hide the sold-out alert banner (called on first new selection). */
  _dismissOosAlert() {
    if (this.oosAlert) {
      this.oosAlert.style.display = 'none';
      this.oosAlert.innerHTML = '';

      /* Re-check sticky position since OOS alert changes sticky height */
      if (this._checkStickyPosition) {
        requestAnimationFrame(() => this._checkStickyPosition());
      }
    }
  }

  /* In integrated PDP mode, only treat BYO as active when the currently
     selected size/dropdown option is a BYO option. */
  _isIntegratedByoSelected() {
    if (!this.isIntegratedPdpByo) return true;

    const selectorRoot = this.root.closest('.pdp-size-selector');
    if (!selectorRoot) return true;

    const activeOpt = selectorRoot.querySelector(
      '.pdp-ss__size-option--active, .pdp-ss__dropdown-option--active'
    );
    if (activeOpt) return activeOpt.dataset.isByo === 'true';

    /* Fallback: if no active option is marked yet, use inline visibility. */
    const inlineWrapper = this.root.closest('.pdp-ss__byo-inline');
    if (inlineWrapper) return inlineWrapper.style.display !== 'none';

    return true;
  }

  /**
   * Write current selections + bundle qty to the URL via replaceState.
   * Non-BYO params are preserved.
   */
  pushToUrl() {
    if (this.embedded) return;
    const params = new URLSearchParams(window.location.search);

    /* Remove our managed params */
    params.delete('flavors');
    params.delete('bundle-qty');

    /* Build flavors string: handle:qty,handle:qty */
    const entries = Object.entries(this.selections);
    if (entries.length > 0 && this._isIntegratedByoSelected()) {
      const flavorsStr = entries
        .map(([vid, qty]) => `${this.variantToHandle[vid]}:${qty}`)
        .join(',');
      params.set('flavors', flavorsStr);
    }

    /* Bundle qty (only write if > 1 to keep URL clean) */
    if (this.bundleQty > 1) {
      params.set('bundle-qty', this.bundleQty);
    }

    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);
  }

  /** For flavor selection changes, anchor to the inline panel or standalone size area. */
  _anchorIfAboveBuilder() {
    const target = this._getInteractionAnchorTarget();
    if (window.innerWidth > 769) {
      this._scrollToDesktop(target, 24);
    } else {
      this._scrollTo(target, 12);
    }
  }

  /** Scroll so the flavor card for `vid` is vertically centered in the visible area. */
  _centerCardInViewport(vid) {
    const card = this.cards.find((el) => el.dataset.variantId === String(vid));
    if (!card) return;

    requestAnimationFrame(() => {
      const cardRect = card.getBoundingClientRect();
      const cardH = cardRect.height;

      if (window.innerWidth > 769) {
        const infoContainer = document.querySelector('.ae--product-information');
          if (infoContainer && infoContainer.scrollHeight > infoContainer.clientHeight) {
            const header = document.querySelector('.ae--header');
            const headerH = header ? header.offsetHeight : 60;
            /* Card center should land at the midpoint of the viewport below the header */
            const viewportCenter = (headerH + window.innerHeight) / 2;
            const targetScrollTop = infoContainer.scrollTop + cardRect.top + cardH / 2 - viewportCenter;
            infoContainer.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
            return;
          }
      }

      /* Mobile: scroll the page so the card is centered in the space below sticky controls */
      const header = document.querySelector('.ae--header');
      const headerH = header ? header.offsetHeight : 60;
      const fanH = (this.mobilePreview && this.mobilePreview.classList.contains('is-visible'))
        ? this.mobilePreview.offsetHeight : 0;
      const stickyH = this.stickyControls ? this.stickyControls.offsetHeight : 0;
      const reservedTop = headerH + fanH + stickyH;
      const availableH = window.innerHeight - reservedTop;
      const targetTop = cardRect.top + window.scrollY - reservedTop - (availableH / 2 - cardH / 2);
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    });
  }

  /** Center a flavor card vertically inside the nearest scrollable BYO container. */
  _centerFlavorInBuilder(vid) {
    const card = this.cards.find((el) => el.dataset.variantId === String(vid));
    if (!card) return;

    let scroller = null;
    let node = card.parentElement;

    while (node) {
      const style = window.getComputedStyle(node);
      const canScrollY = /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;
      if (canScrollY) {
        scroller = node;
        break;
      }
      if (node === this.root) break;
      node = node.parentElement;
    }

    if (!scroller) {
      const rootStyle = window.getComputedStyle(this.root);
      const rootCanScrollY = /(auto|scroll)/.test(rootStyle.overflowY) && this.root.scrollHeight > this.root.clientHeight + 1;
      if (!rootCanScrollY) return;
      scroller = this.root;
    }

    const cardRect = card.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const delta = (cardRect.top - scrollerRect.top) - (scroller.clientHeight / 2 - cardRect.height / 2);
    const rawTarget = scroller.scrollTop + delta;
    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const targetTop = Math.max(0, Math.min(maxScroll, rawTarget));

    scroller.scrollTo({ top: targetTop, behavior: 'smooth' });
  }

  /* ---- mutations ---- */
  increment(vid, interactionType = 'click') {
    if (this.totalSelected() >= this.maxSelections) return;
    const wasEmpty = this.totalSelected() === 0;
    this.selections[vid] = (this.selections[vid] || 0) + 1;
    this._dismissOosAlert();
    this._clearActiveQuickPick();
    this.render();
    this._showMobilePreviewAndAnchor();

    this._trackByo({
      interaction_type: interactionType,
      element_name: 'flavors',
      option_selected: `add ${this._flavorName(vid)}`,
      option_count: this.totalSelected(),
      option_timestamp: Math.floor(Date.now() / 1000),
    });

    this._anchorToSelectASize();
  }

  decrement(vid, interactionType = 'click') {
    if (!this.selections[vid]) return;
    this.selections[vid] -= 1;
    if (this.selections[vid] <= 0) delete this.selections[vid];
    this._dismissOosAlert();
    this._clearActiveQuickPick();
    this.render();
    this._showMobilePreviewAndAnchor();

    this._trackByo({
      interaction_type: interactionType,
      element_name: 'flavors',
      option_selected: `minus ${this._flavorName(vid)}`,
      option_count: this.totalSelected(),
      option_timestamp: Math.floor(Date.now() / 1000),
    });

    this._anchorToSelectASize();
  }

  /** Remove the is-active state from all Quick Pick buttons
      and restore the original page product as the parent. */
  _clearActiveQuickPick() {
    this.quickPicks.forEach((qp) => qp.classList.remove('is-active'));
    this.activeQuickPick = null;

    /* Restore the page product's variant & price */
    this.parentVariantId = this.originalParentVariantId;
    this.basePriceCents = this.originalBasePriceCents;
    this.productPrice = this.originalProductPrice;

    /* Restore page title, price display, and browser URL */
    this._updatePageProduct(
      this.originalProductTitle,
      this.originalProductPrice,
      this.originalProductUrl
    );
  }

  /**
   * Update the visible product title, price, and browser URL.
   * Used when switching between Quick Picks or restoring the original product.
   */
  _updatePageProduct(title, price, url) {
    if (this.embedded) return;
    /* Keep PDP price owned by PDP variant/size logic, not BYO child selections. */

    if (title) {
      const PIVOT = 'Protein Powder';
      const isPackProduct = (u) => u && (u.includes('variety-pack') || u.includes('build-your-own') || u.includes('byo'));
      let mainTitle = title;
      let eyebrow = '';

      if (title.includes(PIVOT)) {
        /* e.g. "Protein Powder Sweet Tooth Variety Pack" */
        const parts = title.split(PIVOT);
        const before = parts[0].trim();
        const after  = (parts[1] || '').trim();
        mainTitle = PIVOT;
        if (before && after) eyebrow = (before + ' ' + after).trim();
        else if (before)     eyebrow = before;
        else if (after)      eyebrow = after;
      } else if (isPackProduct(url)) {
        /* Title has no "Protein Powder" prefix — put whole title in eyebrow */
        eyebrow    = title;
        mainTitle  = PIVOT;
      }

      if (this.pageTitleEl) this.pageTitleEl.textContent = mainTitle;

      /* Create eyebrow element if it doesn't exist yet */
      if (!this.pageEyebrowEl && this.pageTitleEl) {
        const el = document.createElement('p');
        el.className = 'ae--product-eyebrow';
        this.pageTitleEl.parentNode.insertBefore(el, this.pageTitleEl);
        this.pageEyebrowEl = el;
      }
      if (this.pageEyebrowEl) {
        //this.pageEyebrowEl.textContent = eyebrow;
        //this.pageEyebrowEl.style.display = eyebrow ? '' : 'none';
      }

      document.title = title;
    }
  }

  /** On init, if the current page product matches a Quick Pick's
      variant, highlight that button so the user sees which QP is active. */
  _activateMatchingQuickPick() {
    const pageVariant = String(this.originalParentVariantId);
    for (const btn of this.quickPicks) {
      if (btn.dataset.variantId === pageVariant) {
        btn.classList.add('is-active');
        this.activeQuickPick = btn;
        break;
      }
    }
  }

  /* ---- render ---- */
  render() {
    const total = this.totalSelected();
    /* Inline PDP bar spans the full max with a tick at the minimum; standalone bar targets the min. */
    const progressTarget = (this.isIntegratedPdpByo || this.embedded)
      ? this.maxSelections
      : (this.minSelections > 0 ? this.minSelections : this.maxSelections);
    const pct = Math.min(100, (total / progressTarget) * 100);
    const isReady = total >= this.minSelections;
    const isMaxed = total >= this.maxSelections;
    const wasCompleteBefore = this.wasComplete;
    this._refreshComputedPrice();

    /* Keep PDP headline price in sync only while BYO is the active selected option. */
    if (this.pagePriceEl && this.isIntegratedPdpByo && this._isIntegratedByoSelected()) {
      this.pagePriceEl.textContent = this.productPrice;
    }

    /* counter */
    if (this.counterCurrent) this.counterCurrent.textContent = total;
    if (this.counterTarget) {
      const showMaxTarget = total >= this.minSelections;
      this.counterTarget.textContent = showMaxTarget ? this.maxSelections : this.minSelections;
      if (this.counterMinLabel) {
        this.counterMinLabel.style.display = showMaxTarget ? 'none' : '';
      }
    }
    if (this.mobileCounterCurrent) this.mobileCounterCurrent.textContent = total;

    /* per-group counts in category row */
    if (this.groupCountEls.length) {
      const groupCounts = {};
      Object.entries(this.selections).forEach(([vid, qty]) => {
        const item = this.cardData[vid];
        if (!item) return;
        const group = item.group || 'primary';
        groupCounts[group] = (groupCounts[group] || 0) + qty;
      });
      this.groupCountEls.forEach((countEl) => {
        const count = groupCounts[countEl.dataset.groupCount] || 0;
        countEl.textContent = count > 0 ? String(count) : '';
      });
    }

    /* builder heading */
    if (this.builderTitle) {
      this.builderTitle.textContent = isMaxed ? (this.root.dataset.labelDone || 'Flavors Selected') : this.originalBuilderTitle;
    }

    /* Inline PDP message under heading */
    if (this.categoryLabel) {
      if (this.isIntegratedPdpByo) {
        this.categoryLabel.innerHTML = this._inlineCategoryMessageHtml(total);
      } else if (this.defaultCategoryLabel) {
        this.categoryLabel.textContent = this.defaultCategoryLabel;
      }
    }

    /* progress bar */
    if (this.progressFill) this.progressFill.style.width = pct + '%';
    /* Update progressbar ARIA attributes */
    const progressContainer = this.root.querySelector('.byo-builder__progress');
    if (progressContainer) {
      progressContainer.setAttribute('aria-valuemax', progressTarget);
      progressContainer.setAttribute('aria-valuenow', Math.min(total, progressTarget));
    }

    /* ATC button */
    if (this.atcButton) {
      this.atcButton.disabled = !isReady;
      this.atcButton.setAttribute('aria-disabled', !isReady);
      const atcLabel = this._editByoId ? 'Update' : 'Add to Cart';
      if (this.embedded) {
        /* Sheet layout: label on the left, running total on the right (before any pick, the "from" price). */
        const d = this.root.dataset;
        const label = isReady
          ? (this._editByoId ? atcLabel : (d.labelAdd || atcLabel))
          : (d.labelSelectMore || 'Select __count__ More').split('__count__').join(this.remainingToMinimum());
        const price = total === 0 ? (this.root.dataset.zeroPrice || '') : this.productPrice;
        this.atcButton.innerHTML = `<span class="byo-builder__atc-label">${label}</span>` + (price ? `<span class="byo-builder__atc-price">${price}</span>` : '');
      } else this.atcButton.textContent = isReady
        ? `${atcLabel} — ${this.productPrice}`
        : (total === 0
          ? `Select up to ${this.maxSelections}`
          : `Select ${this.remainingToMinimum()} more`);
    }

    /* Mirror BYO state to the regular PDP ATC button when inline */
    this._updatePdpAtcButton(isReady);

    /* cards */
    this.cards.forEach((card) => {
      const vid = card.dataset.variantId;
      const qty = this.selections[vid] || 0;
      const qtyVal = card.querySelector('.byo-card__qty-value');
      if (qtyVal) {
        const changed = qtyVal.textContent !== String(qty);
        qtyVal.textContent = qty;
        if (changed && this.embedded) {
          qtyVal.classList.add('is-pop');
          setTimeout(() => qtyVal.classList.remove('is-pop'), 140);
        }
      }
      card.classList.toggle('is-selected', qty > 0);
      card.classList.toggle('is-maxed', isMaxed && qty === 0);

      /* Disable the + button when at max selections */
      const incBtn = card.querySelector('[data-action="inc"]');
      if (incBtn) incBtn.disabled = isMaxed;
      const addBtn = card.querySelector('.byo-card__add-btn');
      if (addBtn) addBtn.disabled = isMaxed;
    });

    /* Collapse / expand:
       Auto-collapse only on the transition TO complete (not every render).
       Auto-expand when selections drop below the minimum; between min and max
       we respect whatever state the user last chose. */
    if (this.embedded) {
      this.isCollapsed = false;
    } else if (isMaxed && !wasCompleteBefore) {
      this.isCollapsed = true;
    } else if (total < this.minSelections) {
      this.isCollapsed = false;
    }
    this.wasComplete = isMaxed;

    /* Multi-group category header: show plain label only while summary is visible */
    if (this.categoryGroups && this.categoryAllFlavors) {
      this.categoryGroups.style.display = this.isCollapsed ? 'none' : '';
      this.categoryAllFlavors.style.display = this.isCollapsed ? 'inline-flex' : 'none';
    }

    if (this.flavorList) this.flavorList.style.display = this.isCollapsed ? 'none' : '';
    //if (this.progressBar) this.progressBar.style.display = this.isCollapsed ? 'none' : '';
    this._applyGroupVisibility();

    this.root.classList.toggle('byo-builder--summary', this.isCollapsed);
    this.root.classList.toggle('byo-builder--list', !this.isCollapsed);
    this._updateInlineListMaxHeight();

    /* Clear button – visible when any selections exist */
    if (this.clearBtn) {
      this.clearBtn.style.display = total > 0 ? '' : 'none';
    }

    /* Show / Hide button – visible once selections exceed the minimum */
    if (this.showBtn) {
      const showBtnVisible = this.isIntegratedPdpByo
        ? total > this.minSelections
        : isMaxed;
      this.showBtn.style.display = showBtnVisible ? '' : 'none';
      this.showBtn.classList.toggle('is-expanded', !this.isCollapsed);
      this.showBtn.setAttribute('aria-expanded', !this.isCollapsed);
      if (this.showLabel) {
        this.showLabel.textContent = this.isCollapsed ? 'Show' : 'Hide';
      }
    }

    if (this.summaryContainer) {
      /* Always keep the summary DOM in sync with the current selections
         so it never flashes a stale count when Show/Hide is toggled. */
      this.renderSummary();
      if (this.isCollapsed) {
        this.summaryContainer.style.display = '';
        /* Unstick the controls when summary is showing */
        if (this.stickyControls) {
          this.stickyControls.classList.add('byo-sticky--summary-visible');
          this.stickyControls.style.position = 'relative';
          this.stickyControls.style.top = 'auto';
        }
      } else {
        this.summaryContainer.style.display = 'none';
        /* Re-enable sticky when flavor list is showing */
        if (this.stickyControls) {
          this.stickyControls.classList.remove('byo-sticky--summary-visible');
          this.stickyControls.style.position = '';
          this.stickyControls.style.top = '';
          this._updateStickyOffsets();
        }
      }
    }

    /* Re-check sticky position after summary visibility changes */
    if (this._checkStickyPosition) {
      requestAnimationFrame(() => this._checkStickyPosition());
    }

    /* Dynamic product images */
    this.renderDynamicImages();

    /* Sticky footer on mobile — only when selections > 0 */
    if (this.footerEl) {
      this.footerEl.classList.toggle('is-sticky', total > 0);
    }

    /* Re-check PDP sticky ATC when BYO just became complete/collapsed. */
    if (isMaxed && !wasCompleteBefore && !this.embedded) {
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('pdp:layout-changed', {
          detail: { source: 'byo-complete' },
        }));
      });

      /* When auto-collapsing into summary, anchor to Select a Size
         after layout settles so the top lands consistently. */
      if (!this._suppressCompleteScroll) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._anchorIfAboveBuilder();
          });
        });
      }
    }

    /* sync URL */
    this.pushToUrl();
  }

  /* ---- Build grouped summary HTML ---- */
  renderSummary() {
    if (!this.summaryItems) return;

    /* Inline PDP BYO: show product images in a horizontally scrollable fan. */
    if (this.isInline) {
      const packets = [];
      Object.entries(this.selections).forEach(([vid, qty]) => {
        const item = this.cardData[vid];
        if (!item) return;
        for (let i = 0; i < qty; i++) packets.push(item);
      });

      const n = packets.length;
      if (n === 0) { this.summaryItems.innerHTML = ''; return; }

      /* Mirror the mobile-preview-fan sizing: tight overlap, packets scale up
         when few are selected so the fan always reaches the container edges. */
      const widthMap = { 1: 90, 2: 80, 3: 70, 4: 64, 5: 50, 6: 43, 7: 38, 8: 34, 9: 30, 10: 27, 11: 27, 12: 27, 13: 27, 14: 27, 15: 27 };
      const basePacketPct = widthMap[Math.min(n, 15)] || 27;
      const overlapFactor = n === 1 ? 1 : (n <= 4 ? 0.14 : (n < 6 ? 0.18 : 0.22));
      const baseFanPct = basePacketPct + (n - 1) * basePacketPct * overlapFactor;
      /* If the natural fan is narrower than the container, scale the whole
         fan uniformly so the outermost packets reach the container edges. */
      const scale = baseFanPct < 100 ? 100 / baseFanPct : 1;
      const packetWidthPct = basePacketPct * scale;
      const fanWidthPct = baseFanPct * scale;
      const startLeftPct = (100 - fanWidthPct) / 2;
      const stepPct = n > 1 ? (fanWidthPct - packetWidthPct) / (n - 1) : 0;
      const angles = [0, -3, 0, -2, 0, -2, 3, -3, 2, -2, 0, -2, 2, -3, 2].map((a) => {
        const scale = Math.min(1, n / 6);
        return Math.round(a * scale);
      });

      let html = `<div class="byo-summary__track">`;
      packets.forEach((item, i) => {
        const leftPct = startLeftPct + i * stepPct;
        const angle = n === 1 ? 0 : angles[i % angles.length];
        const style = `left:${leftPct.toFixed(2)}%; width:${packetWidthPct}%; z-index:${i}; transform: rotate(${angle}deg);`;
        html += `<div class="byo-summary__img-tile" role="listitem" aria-label="${item.title}" style="${style}">`;
        if (item.productImage) {
          html += `<img src="${item.productImage}" alt="${item.title}" loading="lazy" draggable="false" />`;
        } else {
          html += `<span class="byo-summary__img-fallback" style="background-color:${item.pdpColor || '#eee'};"></span>`;
        }
        html += `</div>`;
      });
      html += `</div>`;
      this.summaryItems.innerHTML = html;
      this._bindSummaryDragScroll();
      this._observeSummaryResize();
      return;
    }

    /*
     * Default (non-inline): render each selected flavor as a pill chip:


    /*
     * Render each selected flavor as its own pill chip:
     *   🟢 FLAVOR NAME  ×qty
     * Laid out in a 2-column grid.
     * Each chip is a button for keyboard accessibility.
     */
    let html = '';
    Object.entries(this.selections).forEach(([vid, qty]) => {
      const item = this.cardData[vid];
      if (!item) return;
      const bg = item.pdpColor || '#ccc';
      html += `
        <button type="button" class="byo-summary__chip" role="listitem" aria-label="${item.title}, quantity ${qty}">
          <span class="byo-summary__dot" style="background-color:${bg};" aria-hidden="true"></span>
          <span class="byo-summary__chip-name">${item.title}</span>
          <span class="byo-summary__chip-qty" aria-hidden="true">×${qty}</span>
        </button>`;
    });

    this.summaryItems.innerHTML = html;
  }

  /* ---- Dynamic product image fan ---- */
  renderDynamicImages() {
    const selected = Object.entries(this.selections);

    /* For inline BYO (inside the size selector), only manipulate productMedia when
       this BYO wrapper is actually the active/visible selection. Otherwise the fan
       would replace the main product image even when a different size is selected. */
    const inlineWrapper = this.isInline ? this.root.closest('.pdp-ss__byo-inline') : null;
    const inlineIsActive = !inlineWrapper || window.getComputedStyle(inlineWrapper).display !== 'none';

    /* Inform PDP media swap logic when exactly one BYO flavor is selected. */
    let singleFlavorMediaProductId = '';
    if (selected.length === 1 && inlineIsActive) {
      const singleVid = selected[0][0];
      const singleData = this.cardData[singleVid];
      singleFlavorMediaProductId = singleData && singleData.productId ? String(singleData.productId) : '';
    }
    if (!this.embedded && this._lastMediaProductIdNotified !== singleFlavorMediaProductId) {
      this._lastMediaProductIdNotified = singleFlavorMediaProductId;
      document.dispatchEvent(new CustomEvent('pdp:byo-media-product-changed', {
        detail: { productId: singleFlavorMediaProductId }
      }));
    }

    /* No selections → clear every stale packet, restore media, and hide preview. */
    if (selected.length === 0) {
      this._forceFanVisible = false;
      if (this.mobilePreviewFan) this.mobilePreviewFan.replaceChildren();
      if (this.mosaicActive && this.productMedia && inlineIsActive) {
        this.productMedia.innerHTML = this.originalMediaHtml;
        this.productMedia.classList.remove('byo-fan-active');
        this.mosaicActive = false;
      }
      this.updateMobilePreviewVisibility();
      return;
    }

    /* Don't replace product media when this inline BYO is not the active size. */
    if (!inlineIsActive) return;

    /*
     * Build an array of packets: one image per qty unit,
     * grouped so same flavors are adjacent.
     */
    const packets = [];
    selected.forEach(([vid, qty]) => {
      const data = this.cardData[vid];
      if (!data || !data.productImage) return;
      for (let i = 0; i < qty; i++) {
        packets.push({ src: data.productImage, title: data.title, handle: data.handle });
      }
    });

    if (packets.length === 0) return;

    const count = packets.length;

    /* ---- Desktop fan (replaces product media) ---- */
    if (this.productMedia) {
      const n = count;

      /* Row layout: packets per row based on total count (spec-defined) */
      const rowLayouts = {
        7:  [4, 3],
        8:  [4, 4],
        9:  [5, 4],
        10: [5, 5],
        11: [4, 4, 3],
        12: [4, 4, 4],
        13: [5, 4, 4],
        14: [5, 5, 4],
        15: [5, 5, 5],
      };
      const rowLayout = n <= 6 ? [n] : (rowLayouts[n] || [n]);
      const numRows = rowLayout.length;

      /* Base packet widths for single-row display, scaled down for multi-row
         so packets fit within their row height. */
      const singleRowWidthMap = { 1: 90, 2: 80, 3: 70, 4: 64, 5: 50, 6: 43 };
      const rowScaleMap = { 1: 1.0, 2: 0.92, 3: 0.86 };
      const rowScale = rowScaleMap[numRows] || 0.72;
      const overlapFactor = 0.28;
      /* Alternating rotation angles (cycled globally across all packets) */
      const angles = [0, -3, 2, -2, 2, -2, 3, -3, 2, -2, 0, -2, 2, -3, 2];

      let globalIdx = 0;
      const rowsHtml = rowLayout.map((rowCount, rowIdx) => {
        const baseWidth = singleRowWidthMap[Math.min(rowCount, 6)] || 43;
        const packetWidth = numRows === 1 ? baseWidth : Math.round(baseWidth * rowScale);
        const rowOverlapFactor = (n === 1) ? 1 : overlapFactor;
        const fanWidth = packetWidth + (rowCount - 1) * packetWidth * rowOverlapFactor;
        const startLeft = (100 - fanWidth) / 2;
        const step = rowCount > 1 ? (fanWidth - packetWidth) / (rowCount - 1) : 0;
        const rowBaseZ = rowIdx * 20;

        let rowHtml = '';
        for (let j = 0; j < rowCount; j++) {
          const p = packets[globalIdx];
          if (!p) break;
          const left = startLeft + j * step;
          const angle = (n === 1) ? 0 : angles[globalIdx % angles.length];
          rowHtml += `<div class="byo-fan__packet" style="width:${packetWidth}%; left:${left.toFixed(1)}%; z-index:${rowBaseZ + j}; transform: translateY(-50%) rotate(${angle}deg);">
              <img src="${p.src}" alt="${p.title}" />
            </div>`;
          globalIdx++;
        }
        return `<div class="byo-fan__row">${rowHtml}</div>`;
      }).join('');

      this.productMedia.innerHTML = `
        <div class="byo-fan" data-count="${n}" data-rows="${numRows}">
          ${rowsHtml}
        </div>`;
      this.productMedia.classList.add('byo-fan-active');
      this.mosaicActive = true;

      /* Pull rows together so each overlaps the one above by ~55% of row height.
         Must run after layout so offsetHeight is available. */
      if (numRows > 1) {
        requestAnimationFrame(() => {
          const fanEl = this.productMedia.querySelector('.byo-fan');
          const rowEls = this.productMedia.querySelectorAll('.byo-fan__row');
          const baseRowHeight = fanEl
            ? (fanEl.clientHeight / numRows)
            : (rowEls[0] ? rowEls[0].offsetHeight : 0);
          const rowOverlapRatio = window.innerWidth <= 769 ? 0.95 : 1.3;
          const overlapPx = Math.round(baseRowHeight * rowOverlapRatio);

          rowEls.forEach((row, i) => {
            row.style.zIndex = i + 1; /* later rows paint on top */
            row.style.marginTop = i > 0 ? `-${overlapPx}px` : '0px';
          });
        });
      }
    }

    /* ---- Embedded sheet: one row of packs resting on the button, sized as the design does ---- */
    if (this.embedded && this.mobilePreviewFan) {
      const n = packets.length;
      const ratio = 2.0102;                       /* pack width / spacing, as in the design */
      const step = n <= 8 ? 9.85 : 98.8 / (n - 1 + ratio);
      const width = step * ratio;
      const angles = [-2.0, 1.3, -1.5, 1.9, -1.0, 1.7, -1.8, 1.1];
      /* Packs are kept between renders so they glide to their new place; new ones fade in, removed ones fade out. */
      const fan = this.mobilePreviewFan;
      const live = Array.from(fan.children).filter((el) => !el.classList.contains('is-leave'));
      packets.forEach((p, i) => {
        const left = 50 + (i - (n - 1) / 2) * step;
        /* pack-only art when the host page has it, otherwise the product photo */
        const art = (this.root.__packArt && this.root.__packArt(p.handle)) || p.src;
        let el = live[i];
        if (!el) {
          el = document.createElement('div');
          el.className = 'byo-mobile-fan__packet is-enter';
          el.innerHTML = '<img alt="" />';
          fan.appendChild(el);
          requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('is-enter')));
        }
        const img = el.querySelector('img');
        if (img.getAttribute('src') !== art) img.setAttribute('src', art);
        el.style.left = `${left.toFixed(3)}%`;
        el.style.width = `${width.toFixed(3)}%`;
        el.style.zIndex = i + 1;
        el.style.transform = `translateX(-50%) rotate(${angles[i % 8]}deg)`;
      });
      live.slice(n).forEach((el) => {
        el.classList.add('is-leave');
        setTimeout(() => el.remove(), 320);
      });
      this.updateMobilePreviewVisibility();
      return;
    }

    /* ---- Mobile preview strip (always single row) ---- */
    if (this.mobilePreviewFan) {
      const maxAngle = Math.min(3 + count * 1, 12);
      const n = count;

      /* Dynamic sizing per packet count */
      const widthMap  = { 1: 90, 2: 80, 3: 70, 4: 64, 5: 50, 6: 43, 7: 38, 8: 34, 9: 30, 10: 27, 11: 27, 12: 27, 13: 27, 14: 27, 15: 27 };
      const packetWidth = widthMap[Math.min(n, 15)] || 27;

      /* Compute total width of all overlapping packets:
         Each additional packet adds (packetWidth * overlapFactor) to the total.
         overlapFactor: how much of each new packet is visible (not hidden behind prev). */
      const overlapFactor = n === 1 ? 1 : (n <= 4 ? 0.14 : (n < 6 ? 0.18 : 0.22));
      const fanWidth = packetWidth + (n - 1) * packetWidth * overlapFactor;

      const startLeft = (100 - fanWidth) / 2;
      const step = n > 1 ? (fanWidth - packetWidth) / (n - 1) : 0;
      const angles = [0, -3, 0, -2, 0, -2, 3, -3, 2, -2, 0, -2, 2, -3, 2].map((a, i) => {
        /* Spread angles more for larger counts, up to a max. */
        const scale = Math.min(1, n / 6);
        return Math.round(a * scale);
      });
      const mobileHtml = packets
        .map((p, i) => {
          const angle = n === 1 ? 0 : angles[i % angles.length];
          const left = startLeft + i * step;
          return `<div class="byo-mobile-fan__packet" style="width:${packetWidth}%; left:${left.toFixed(1)}%; z-index:${i + 1}; transform: rotate(${angle}deg);">
              <img src="${p.src}" alt="${p.title}" />
            </div>`;
        })
        .join('');

      this.mobilePreviewFan.innerHTML = mobileHtml;
      this.updateMobilePreviewVisibility();
    }
  }

  /* ---- Add to cart via Cart AJAX with nested lines (parent_id) ---- */
  async addToCart() {
    if (this.totalSelected() < this.minSelections) return;

    this.atcButton.disabled = true;
    this.atcButton.textContent = this._editByoId ? 'Updating…' : 'Adding…';

    /* Build the items array:
       First item is the parent bundle product.
       Child items reference the parent via parent_id (variant id).

       A _byo_id fingerprint on the parent ensures that two BYO bundles
       with different flavor selections stay as separate cart line items
       instead of merging into one. Children carry _byo_id too so the
       Liquid cart template can group them under the correct parent. */
    const sortedEntries = Object.entries(this.selections).sort(([a], [b]) => a.localeCompare(b));
    const fingerprint = sortedEntries.map(([vid, qty]) => `${vid}:${qty}`).join(',');
    const byoId = fingerprint;
    const selectionAdjustmentCents = this._totalSelectionAdjustmentCents();
    const dynamicUnitPriceCents = this._computeDynamicPriceCents();

    /* Build a URL that links back to the BYO product with selections pre-filled.
       Include edit-byo-id so clicking ATC from that link replaces the old bundle. */
    const flavorsStr = sortedEntries
      .map(([vid, qty]) => `${this.variantToHandle[vid]}:${qty}`)
      .join(',');
    const byoParams = new URLSearchParams();
    byoParams.set('flavors', flavorsStr);
    if (this.bundleQty > 1) byoParams.set('bundle-qty', this.bundleQty);
    const byoUrl = this.originalProductUrl + '?' + byoParams.toString();
    byoParams.set('edit-byo-id', byoId);
    const byoEditUrl = this.originalProductUrl + '?' + byoParams.toString();

    const isQuickPick = !!this.activeQuickPick;
    const childProductIds = sortedEntries.map(([vid]) => this._variantProductId[vid] || vid).join(',');
    const byoVariantStr = isQuickPick ? `qp: ${childProductIds}` : childProductIds;

    const items = [
      {
        id: this.parentVariantId,
        quantity: this.bundleQty,
        properties: {
          _byo_id: byoId,
          _byo_url: byoUrl,
          _byo_edit_url: byoEditUrl,
          _byo_is_quickpick: isQuickPick ? 'true' : 'false',
          _byo_variant_str: byoVariantStr,
          _byo_dynamic_unit_price: this._formatMoney(dynamicUnitPriceCents),
          _byo_child_price_adjustment: this._formatMoney(selectionAdjustmentCents),
        },
      },
    ];

    sortedEntries.forEach(([variantId, quantity]) => {
      const childProperties = {
        _parent_variant_id: String(this.parentVariantId),
        _byo_id: byoId,
      };

      const shouldForceBundledWith = this.isIntegratedPdpByo && this._parentHasNonZeroPrice();
      if (this.parentEnablesBundledWith || shouldForceBundledWith) {
        childProperties._bundled_with = String(this.parentVariantId);
      }

      items.push({
        id: parseInt(variantId, 10),
        quantity: quantity * this.bundleQty,
        parent_id: this.parentVariantId,
        properties: childProperties,
      });
    });

    try {
      /* If editing an existing bundle, remove the old line items first */
      if (this._editByoId) {
        const cartRes = await fetch('/cart.js', { credentials: 'include' });
        const cartData = await cartRes.json();
        const updates = {};
        cartData.items.forEach((ci) => {
          if (ci.properties && ci.properties._byo_id === this._editByoId) {
            updates[ci.key] = 0;
          }
        });
        if (Object.keys(updates).length > 0) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ updates }),
          });
        }
        this._editByoId = null;
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }

      /* Reset selections */
      this.selections = {};
      this.isCollapsed = false;
      this.setBundleQty(1);
      this.render();

      /* Navigate to the cart page (embedded builders report back instead) */
      if (this.embedded) {
        this.root.dispatchEvent(new CustomEvent('byo:added', { bubbles: true, detail: { items } }));
        return;
      }
      window.location.href = '/cart';
    } catch (err) {
      console.error('[BYO Builder]', err);
      if (this.embedded) {
        this.root.dispatchEvent(new CustomEvent('byo:error', { bubbles: true, detail: { message: err.message || 'Something went wrong. Please try again.' } }));
      } else {
        alert(err.message || 'Something went wrong. Please try again.');
      }
      this.atcButton.disabled = false;
      const fallbackLabel = this._editByoId ? 'Update' : 'Add to Cart';
      if (this.embedded) this.render(); else this.atcButton.textContent = `${fallbackLabel} — ${this.productPrice}`;
    }
  }
}

window.BYOBuilder = BYOBuilder;

/* ---- auto-init ---- */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.byo-builder').forEach((el) => {
    /* Guard against duplicate init (auto-init + pdp-size-selector's initByoBuilder). */
    if (el._byoInstance || el.dataset.byoInitialized === '1') return;
    el.dataset.byoInitialized = '1';
    el._byoInstance = new BYOBuilder(el);
  });
});
}
